import { brightsFor, type Palette } from "@/color/generate";
import { type Hex, mix, normalizeHex, rgbToHex, shade, toLch } from "@/color/oklch";
import { newId, parseTheme, SCHEMA_URL, SCHEMA_VERSION, type Theme } from "@/model/theme";
import { type TerminalColors, themeFromTerminal } from "./terminal";

export type ImportFormat =
  | "theme"
  | "ghostty"
  | "alacritty"
  | "kitty"
  | "iterm"
  | "base16"
  | "unknown";

export const IMPORT_FORMATS: {
  id: Exclude<ImportFormat, "unknown">;
  label: string;
  hint: string;
}[] = [
  { id: "theme", label: "Theme JSON", hint: ".json exported from themes.utimura.io" },
  { id: "ghostty", label: "Ghostty", hint: "theme file or config with palette = N=#hex" },
  { id: "alacritty", label: "Alacritty", hint: "alacritty.toml [colors.*]" },
  { id: "kitty", label: "Kitty", hint: "kitty.conf / theme .conf" },
  { id: "iterm", label: "iTerm2", hint: ".itermcolors" },
  { id: "base16", label: "Base16 / Base24", hint: "tinted-theming scheme YAML" },
];

export function detectFormat(text: string, filename = ""): ImportFormat {
  const name = filename.toLowerCase();
  const t = text.trimStart();
  if (t.startsWith("{") && /"palette"\s*:/.test(t) && /"variant"\s*:/.test(t)) return "theme";
  if (name.endsWith(".itermcolors") || /<key>Ansi 0 Color<\/key>/.test(t)) return "iterm";
  if (/^\s*palette\s*=\s*\d+\s*=/m.test(t)) return "ghostty";
  if (/^\s*\[colors\.(primary|normal|bright)\]/m.test(t)) return "alacritty";
  if (/^\s*color\d+\s+#?[0-9a-f]{6}/im.test(t)) return "kitty";
  if (/^\s*base0[0-9a-f]\s*:/im.test(t)) return "base16";
  return "unknown";
}

/** Parses any supported format into a theme. Throws with a readable message on failure. */
export function importTheme(text: string, filename = ""): { theme: Theme; format: ImportFormat } {
  const format = detectFormat(text, filename);
  const base = filename
    .replace(/\.[^.]+$/, "")
    .replace(/[-_]+/g, " ")
    .trim();
  switch (format) {
    case "theme": {
      const theme = parseTheme(JSON.parse(text));
      return { theme: { ...theme, id: newId() }, format };
    }
    case "ghostty":
      return { theme: themeFromTerminal(parseGhostty(text, base)), format };
    case "alacritty":
      return { theme: themeFromTerminal(parseAlacritty(text, base)), format };
    case "kitty":
      return { theme: themeFromTerminal(parseKitty(text, base)), format };
    case "iterm":
      return { theme: themeFromTerminal(parseIterm(text, base)), format };
    case "base16":
      return { theme: parseBase16(text, base), format };
    default:
      throw new Error(
        "Unrecognized format. Supported: theme JSON, Ghostty, Alacritty, Kitty, iTerm2 (.itermcolors) and base16/base24 YAML.",
      );
  }
}

function hex(value: string | undefined): Hex | undefined {
  if (!value) return undefined;
  return normalizeHex(value.replace(/^0x/i, "").replace(/['"]/g, "")) ?? undefined;
}

function finish(partial: Partial<TerminalColors>, name: string): TerminalColors {
  if (!partial.background || !partial.foreground) {
    throw new Error("The file has no background and foreground colors.");
  }
  return { name, ansi: [], ...partial } as TerminalColors;
}

export function parseGhostty(text: string, name: string): TerminalColors {
  const out: Partial<TerminalColors> = { ansi: [] };
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (line.startsWith("#")) continue;
    const m = /^([a-z-]+)\s*=\s*(.+)$/i.exec(line);
    if (!m?.[1] || !m[2]) continue;
    const key = m[1];
    const value = m[2].trim();
    if (key === "palette") {
      const p = /^(\d+)\s*=\s*(#?[0-9a-f]{6}|#?[0-9a-f]{3})$/i.exec(value);
      if (p?.[1] && Number(p[1]) < 16) (out.ansi as (Hex | undefined)[])[Number(p[1])] = hex(p[2]);
      continue;
    }
    const map: Record<string, keyof TerminalColors> = {
      background: "background",
      foreground: "foreground",
      "cursor-color": "cursor",
      "cursor-text": "cursorText",
      "selection-background": "selectionBg",
      "selection-foreground": "selectionFg",
    };
    const field = map[key];
    const color = hex(value);
    if (field && color) (out as Record<string, unknown>)[field] = color;
  }
  return finish(out, name);
}

export function parseAlacritty(text: string, name: string): TerminalColors {
  const out: Partial<TerminalColors> = { ansi: [] };
  const order = ["black", "red", "green", "yellow", "blue", "magenta", "cyan", "white"];
  let section = "";
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    const sec = /^\[(.+)\]$/.exec(line);
    if (sec?.[1]) {
      section = sec[1].trim();
      continue;
    }
    const m = /^([a-z_]+)\s*=\s*["']?([^"'#\s]*#?[0-9a-fx]+)["']?/i.exec(line);
    if (!m?.[1]) continue;
    const key = m[1];
    const color = hex(m[2]);
    if (!color) continue;
    const ansi = out.ansi as (Hex | undefined)[];
    if (section === "colors.primary" && key === "background") out.background = color;
    else if (section === "colors.primary" && key === "foreground") out.foreground = color;
    else if (section === "colors.cursor" && key === "cursor") out.cursor = color;
    else if (section === "colors.cursor" && key === "text") out.cursorText = color;
    else if (section === "colors.selection" && key === "background") out.selectionBg = color;
    else if (section === "colors.selection" && key === "text") out.selectionFg = color;
    else if (section === "colors.normal" && order.includes(key)) ansi[order.indexOf(key)] = color;
    else if (section === "colors.bright" && order.includes(key))
      ansi[order.indexOf(key) + 8] = color;
  }
  return finish(out, name);
}

export function parseKitty(text: string, name: string): TerminalColors {
  const out: Partial<TerminalColors> = { ansi: [] };
  let title = name;
  for (const raw of text.split("\n")) {
    const meta = /^##\s*name:\s*(.+)$/i.exec(raw.trim());
    if (meta?.[1]) title = meta[1].trim();
    const m = /^([a-z_0-9]+)\s+(#?[0-9a-f]{6}|#?[0-9a-f]{3})\b/i.exec(raw.trim());
    if (!m?.[1]) continue;
    const key = m[1];
    const color = hex(m[2]);
    if (!color) continue;
    const idx = /^color(\d+)$/.exec(key);
    if (idx?.[1] && Number(idx[1]) < 16) (out.ansi as (Hex | undefined)[])[Number(idx[1])] = color;
    else if (key === "background") out.background = color;
    else if (key === "foreground") out.foreground = color;
    else if (key === "cursor") out.cursor = color;
    else if (key === "cursor_text_color") out.cursorText = color;
    else if (key === "selection_background") out.selectionBg = color;
    else if (key === "selection_foreground") out.selectionFg = color;
  }
  return finish(out, title);
}

export function parseIterm(text: string, name: string): TerminalColors {
  const out: Partial<TerminalColors> = { ansi: [] };
  const entry = /<key>([^<]+)<\/key>\s*<dict>([\s\S]*?)<\/dict>/g;
  const component = (body: string, which: string) => {
    const m = new RegExp(`<key>${which} Component</key>\\s*<real>([^<]+)</real>`).exec(body);
    return m?.[1] ? Number(m[1]) : 0;
  };
  for (const m of text.matchAll(entry)) {
    const key = m[1]?.trim() ?? "";
    const body = m[2] ?? "";
    const color = rgbToHex(
      component(body, "Red") * 255,
      component(body, "Green") * 255,
      component(body, "Blue") * 255,
    );
    const ansi = /^Ansi (\d+) Color$/.exec(key);
    if (ansi?.[1] && Number(ansi[1]) < 16)
      (out.ansi as (Hex | undefined)[])[Number(ansi[1])] = color;
    else if (key === "Background Color") out.background = color;
    else if (key === "Foreground Color") out.foreground = color;
    else if (key === "Cursor Color") out.cursor = color;
    else if (key === "Cursor Text Color") out.cursorText = color;
    else if (key === "Selection Color") out.selectionBg = color;
    else if (key === "Selected Text Color") out.selectionFg = color;
  }
  return finish(out, name);
}

export function parseBase16(text: string, fallbackName: string): Theme {
  const colors: Record<string, Hex> = {};
  for (const m of text.matchAll(/^\s*(base[0-9a-f]{2})\s*:\s*["']?#?([0-9a-f]{6})["']?/gim)) {
    if (m[1] && m[2]) colors[m[1].toLowerCase()] = `#${m[2].toLowerCase()}`;
  }
  const name =
    /^\s*(?:name|scheme)\s*:\s*["']?([^"'\n]+)["']?/im.exec(text)?.[1]?.trim() || fallbackName;
  const b = (k: string): Hex => {
    const v = colors[k];
    if (!v) throw new Error(`The scheme is missing ${k}.`);
    return v;
  };
  const variant = toLch(b("base00")).l < 0.6 ? "dark" : "light";
  const partial = {
    bg0: colors.base10 ?? shade(b("base00"), -0.03),
    bg1: b("base00"),
    bg2: b("base01"),
    bg3: b("base02"),
    bg4: mix(b("base02"), b("base03"), 0.4),
    fg3: b("base03"),
    fg2: b("base04"),
    fg1: b("base05"),
    fg0: b("base06"),
    red: b("base08"),
    orange: b("base09"),
    yellow: b("base0a"),
    green: b("base0b"),
    cyan: b("base0c"),
    blue: b("base0d"),
    purple: b("base0e"),
    pink: b("base0f"),
  };
  const brights = colors.base12
    ? {
        brightRed: b("base12"),
        brightYellow: b("base13"),
        brightGreen: b("base14"),
        brightCyan: b("base15"),
        brightBlue: b("base16"),
        brightPurple: b("base17"),
      }
    : brightsFor(partial as Palette, variant);
  const now = new Date().toISOString();
  return {
    $schema: SCHEMA_URL,
    version: SCHEMA_VERSION,
    id: newId(),
    name,
    variant,
    palette: { ...partial, ...brights } as Palette,
    overrides: {},
    styles: {},
    locks: [],
    meta: { createdAt: now, updatedAt: now, basedOn: name },
  };
}
